import type { TimetableFile } from "@teamna/shared";

/**
 * The browser side of the timetable reader: turn a picked file into what `/timetable/extract` takes.
 *
 * The reader refuses an image over 5 MB, and a phone photo is often larger. Such a photo is drawn
 * again as a JPEG of at most `TIMETABLE_IMAGE_SIDE` pixels on the long side: still clear enough to
 * read the times, and much smaller. A screenshot that is small enough goes as it is, so its text
 * stays sharp. A PDF always goes as it is.
 */

/** The image types the reader takes as they are. */
const READER_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
/** Under the reader's 5 MB limit, with room for the base64 growth. */
export const TIMETABLE_IMAGE_MAX_BYTES = 3.5 * 1024 * 1024;
/** The long side of a photo that is drawn again. */
export const TIMETABLE_IMAGE_SIDE = 2000;

/** True when the file must be drawn again before it goes to the reader. */
export function needsShrink(file: Pick<File, "type" | "size">): boolean {
  if (!file.type.startsWith("image/")) return false;
  return !READER_IMAGE_TYPES.includes(file.type) || file.size > TIMETABLE_IMAGE_MAX_BYTES;
}

function base64Of(dataUrl: string): string {
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(base64Of(String(reader.result ?? "")));
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("image did not load"));
    image.src = url;
  });
}

/** The photo as a JPEG under the size limit, or null when this browser cannot open it. */
async function shrink(file: File): Promise<string | null> {
  const url = URL.createObjectURL(file);
  try {
    // The browser turns the photo the right way up from its camera data (image-orientation).
    const image = await loadImage(url);
    const scale = Math.min(1, TIMETABLE_IMAGE_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.imageSmoothingQuality = "high";
    // A white floor, so a transparent PNG does not turn black in the JPEG.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.85, 0.75, 0.65]) {
      const base64 = base64Of(canvas.toDataURL("image/jpeg", quality));
      if ((base64.length * 3) / 4 <= TIMETABLE_IMAGE_MAX_BYTES) return base64;
    }
    return null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** One prepared file and its size, or the size that did not fit. */
export type PreparedTimetableFile = { fits: true; file: TimetableFile; bytes: number } | { fits: false; bytes: number };

/**
 * What the reader gets for one picked file, when it fits in `budget` bytes. A file that does not fit
 * is never read whole; `bytes` is then the size that was checked.
 */
export async function timetableFile(file: File, budget: number): Promise<PreparedTimetableFile> {
  if (needsShrink(file)) {
    const data = await shrink(file);
    if (data) {
      const bytes = (data.length * 3) / 4;
      return bytes <= budget ? { fits: true, file: { data, mimeType: "image/jpeg" }, bytes } : { fits: false, bytes };
    }
  }
  // A PDF, a small image, or a photo this browser cannot open: the reader tries the file itself.
  if (file.size > budget) return { fits: false, bytes: file.size };
  const data = await readAsBase64(file);
  return { fits: true, file: { data, mimeType: file.type || "application/octet-stream" }, bytes: file.size };
}
